/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import { SxProps, Theme } from '@mui/material';

import Modal from 'components/_layouts/Modal';

type ModalProps = {
  actions?: React.ReactNode;
  size?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  subtitle?: string | null;
  onSubmit?: () => void;
  sx?: SxProps<Theme>;
};

export default function useModal(): [
  JSX.Element | null,
  (title: string, showModal: (onClose: () => void) => JSX.Element, modalProps?: ModalProps) => void,
] {
  const [modalContent, setModalContent] = useState<null | {
    closeOnClickOutside: boolean;
    content: JSX.Element;
    title: string;
    modalProps?: ModalProps;
  }>(null);

  const onClose = useCallback(() => {
    setModalContent(null);
  }, []);

  const modal = useMemo(() => {
    if (modalContent === null) {
      return null;
    }
    const { title, content, modalProps } = modalContent;
    return (
      <Modal
        onClose={onClose}
        title={title}
        open
        {...modalProps}
      >
        {content}
      </Modal>
    );
  }, [modalContent, onClose]);

  const showModal = useCallback(
    (
      title: string,
      getContent: (onClose: () => void) => JSX.Element,
      modalProps?: ModalProps,
      closeOnClickOutside = false
    ) => {
      setModalContent({
        closeOnClickOutside,
        content: getContent(onClose),
        title,
        modalProps,
      });
    },
    [onClose]
  );

  return [modal, showModal];
}
